import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import { useSetRecoilState } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';

import { updatePractice } from 'api/Practice';

import { DirtyTracker } from 'lib/context/DirtyTrackerContext';
import { useCurrentPractice } from 'lib/hooks/queries/practice/useCurrentPractice';
import { usePracticeLocations } from 'lib/hooks/queries/practice/usePracticeLocations';

import Button from 'components/shared/Button/Button';
import Divider from 'components/shared/Divider/Divider';
import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import PageHeader from 'components/shared/PageHeader/PageHeader';
import { currentPractice } from 'components/practice/practiceState';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import usePracticeNameChecks from 'components/shared/PracticeNameInput/lib/usePracticeNameChecks';

import ScreenWrapper from '../ScreenWrapper';

import formInitialValues from './lib/formInitialValues';
import PracticeIdentity from './components/PracticeIdentity';
import PracticeLocation from './components/PracticeLocation';
import PracticeTimezone from './components/PracticeTimezone';
import formValidationSchema from './lib/formValidationSchema';
import OperationalSettings from './components/OperationalSettings';
import PracticeWorkingDays from './components/PracticeWorkingDays';
import ConsentModal from './components/TechnologyConsent/ConsentModal';
import PracticeBranding from './components/PracticeBranding/PracticeBranding';
import PaymentLinkSettings from './components/PaymentLinkSettings/PaymentLinkSettings';
import {
  getDuplicateLocationLabel,
  getKeepAsTypedFlags,
  getUpdateFields,
  handleLocationsSubmit,
  hasAddressChanged,
  hasLocationsChanged,
  hasPracticeFieldsChanged
} from './lib/helpers';

const ReinitializeForm = ({ practice, resetForm, locations }) => {
  useEffect(() => {
    resetForm({ values: formInitialValues(practice, locations) });
  }, [practice, locations]);
  return null;
};

const PracticeDetails = () => {
  const addressRef = useRef();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setPractice = useSetRecoilState(currentPractice);
  const [showTechnologyModal, setShowTechnologyModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const { registerNameCheck, waitForNameChecks } = usePracticeNameChecks();

  const { data, isLoading } = useCurrentPractice();
  const { data: locationsData } = usePracticeLocations();
  const locations = locationsData?.locations;
  const formikRef = useRef();

  const practice = data?.practice;

  useEffect(() => {
    setPractice((prev) => ({
      ...prev,
      ...(practice || {})
    }));
  }, [practice]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (await waitForNameChecks()) return setSubmitting(false);

    const duplicateLabel = getDuplicateLocationLabel(values);
    if (duplicateLabel) {
      showAlert({
        title: 'Duplicate location label',
        message: `"${duplicateLabel}" is already used by another location. Location labels must be unique.`,
        color: 'danger'
      });
      setSubmitting(false);
      return;
    }

    const initialValues = formInitialValues(practice, locations);
    const addressChanged = hasAddressChanged(values?.address, initialValues?.address);
    const practiceChanged = hasPracticeFieldsChanged(values, initialValues);
    const locationsChanged = hasLocationsChanged(values, initialValues);

    if (!addressChanged && !practiceChanged && !locationsChanged) {
      setSubmitting(false);
      return;
    }

    let updateSuccessful = true;

    // Only update address if it has changed
    if (addressChanged) await addressRef.current?.updateAddress();

    // Only update practice if fields have changed
    if (practiceChanged) {
      const updateFields = getUpdateFields(values, initialValues);

      const res = await updatePractice(navigate, {
        practice_id: practice?.id,
        fields: updateFields,
        ...getKeepAsTypedFlags(updateFields, values)
      });

      if (res.code !== 0) {
        updateSuccessful = false;
      }
    }

    if (locationsChanged) {
      const locationsSuccessful = await handleLocationsSubmit(values, initialValues, { navigate });
      if (!locationsSuccessful) updateSuccessful = false;
      await queryClient.invalidateQueries(['practiceLocations']);
    }

    if (updateSuccessful) {
      await queryClient.invalidateQueries(['currentPractice']);

      showAlert({
        title: 'Practice details updated',
        color: 'success'
      });
    }

    setSubmitting(false);
  };

  if (isLoading && !practice)
    return (
      <ScreenWrapper title="Practice details">
        <Skeleton count={4} height={40} containerClassName="w-full mb-10" />
      </ScreenWrapper>
    );

  return (
    <div className="h-full w-full">
      <Formik
        key={isLoading ? 'loading' : 'loaded'}
        innerRef={formikRef}
        initialValues={formInitialValues(practice, locations)}
        validationSchema={formValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}>
        {({ dirty, resetForm, isSubmitting, setFieldValue }) => (
          <DirtyTracker isDirty={dirty}>
            <Form className="flex h-full w-full flex-col">
              <PageHeader title="Practice details" className="shrink-0" />
              <ReinitializeForm practice={practice} resetForm={resetForm} locations={locations} />
              <div className="flex flex-1 flex-col overflow-y-auto pb-8">
                <div className="flex w-[770px] flex-col gap-6 self-center p-4">
                  <PracticeIdentity registerNameCheck={registerNameCheck} />
                  <Divider />
                  <PracticeBranding />
                  <Divider />
                  <PracticeLocation ref={addressRef} />
                  <Divider />
                  <PracticeTimezone />
                  <Divider />
                  <PracticeWorkingDays />
                  <Divider />
                  <OperationalSettings setShowTechnologyModal={setShowTechnologyModal} />
                  <PaymentLinkSettings practice={practice} />
                </div>
              </div>
              {dirty && (
                <div className="flex items-center justify-end gap-2 border-0 border-t border-solid border-neutral-200 bg-white p-3">
                  <Button
                    text="Cancel"
                    variant="tertiary"
                    color="neutral"
                    size="small"
                    onClick={() => setShowUnsavedModal(true)}
                    disabled={isSubmitting}
                  />
                  <Button
                    text="Update"
                    color="primary"
                    size="small"
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </div>
              )}
              <ConsentModal
                showTechnologyModal={showTechnologyModal}
                hideTechnologyModal={() => setShowTechnologyModal(false)}
                setPractice={setPractice}
                practice={practice}
                updateSurcharge={() => setFieldValue('surchargeEnabled', true)}
              />
              <Confirm
                type="unsaved-changes"
                isOpen={showUnsavedModal}
                handleClose={() => setShowUnsavedModal(false)}
                title="Discard unsaved changes?"
                message="Your unsaved changes will be discarded."
                footerActions={{
                  keepEditing: true,
                  primary: {
                    text: 'Discard',
                    onClick: () => {
                      resetForm();
                      setShowUnsavedModal(false);
                    },
                    color: 'danger'
                  },
                  secondary: null
                }}
              />
            </Form>
          </DirtyTracker>
        )}
      </Formik>
    </div>
  );
};

export default withErrorBoundary(PracticeDetails);
